import type { FleetVehicleRequest } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  PlateInput,
  Select,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardPen } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPlateDisplay } from "../../features/customers/personal-data-values.js";
import { VehicleRequestStatusBadge } from "../../features/fleet/shared.js";
import { usePortal } from "../../features/portal/portal-context.js";
import { usePortalProducts, usePortalVehicleRequests } from "../../features/portal/queries.js";
import { PortalHeader } from "../../features/portal/shared.js";
import { api } from "../../lib/api.js";
import { useCursorPages } from "../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../lib/errors.js";
import { formatDateTime } from "../../lib/format.js";
import { queryKeys } from "../../lib/query.js";
import { usePermissions } from "../../lib/session.js";

export const Route = createFileRoute("/portal/talepler")({ component: PortalRequestsRoute });

/**
 * Araç ekleme talepleri (ADR 0017 §17): filo kullanıcısı talep açar, istasyon personeli onaylayınca araç filoya eklenir.
 * Talep gönderme ayrı izindir (`fleet.portal.vehicle.request`); yakıt `GET /fleet-portal/products`'tan seçilir (carinin
 * şirketlerinin istasyonlarındaki akaryakıt / LPG ürünleri) — personel onayda değiştirebilir.
 */
function PortalRequestsRoute(): ReactElement {
  const { t } = useTranslation();
  const portal = usePortal();
  const permissions = usePermissions();
  const pages = useCursorPages(portal.counterpartyId);
  const list = usePortalVehicleRequests(portal.counterpartyId, pages.cursor);
  const products = usePortalProducts(portal.counterpartyId);
  const productNames = useMemo(
    () => new Map((products.data ?? []).map((product) => [product.id, product.name])),
    [products.data],
  );

  const columns = useMemo<DataTableColumn<FleetVehicleRequest>[]>(
    () => [
      {
        id: "createdAt",
        header: t("portal.fields.requestedAt"),
        hideable: false,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
      },
      {
        id: "plate",
        header: t("portal.fields.plate"),
        cell: (row) => <span className="font-mono">{formatPlateDisplay(row.plate)}</span>,
      },
      {
        id: "vehicle",
        header: t("portal.fields.vehicle"),
        cell: (row) =>
          [row.brand, row.model].filter(Boolean).join(" ") || (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "fuel",
        header: t("portal.fields.fuel"),
        cell: (row) =>
          row.fuelProductId ? (
            (productNames.get(row.fuelProductId) ?? <span className="text-muted-foreground">—</span>)
          ) : (
            <span className="text-muted-foreground">{t("portal.requests.noFuel")}</span>
          ),
      },
      {
        id: "status",
        header: t("portal.fields.status"),
        cell: (row) => <VehicleRequestStatusBadge status={row.status} />,
      },
      {
        id: "decision",
        header: t("portal.fields.decision"),
        cell: (row) =>
          row.decidedAt ? (
            <span className="grid gap-0.5">
              <span className="tabular text-xs text-muted-foreground">{formatDateTime(row.decidedAt)}</span>
              {row.decisionNote ? <span>{row.decisionNote}</span> : null}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [t, productNames],
  );

  return (
    <>
      <PortalHeader title={t("portal.requests.title")} description={t("portal.requests.description")} />
      <div className="grid gap-4">
        {permissions.has("fleet.portal.vehicle.request") ? <RequestForm /> : null}
        <DataTable
          caption={t("portal.requests.caption")}
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
          emptyState={
            <EmptyState
              headingLevel={2}
              icon={<ClipboardPen />}
              title={t("portal.requests.emptyTitle")}
              description={t("portal.requests.emptyDescription")}
            />
          }
          pagination={{
            hasPrevious: pages.hasPrevious,
            hasNext: list.data?.page.hasMore ?? false,
            onPrevious: pages.previous,
            onNext: () => pages.next(list.data?.page.nextCursor),
            pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
          }}
        />
      </div>
    </>
  );
}

function RequestForm(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const portal = usePortal();
  const products = usePortalProducts(portal.counterpartyId);
  const [plate, setPlate] = useState("");
  const [fuelProductId, setFuelProductId] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [note, setNote] = useState("");
  const [plateError, setPlateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/fleet-portal/vehicle-requests", {
          body: {
            counterpartyId: portal.counterpartyId,
            plate,
            brand: brand.trim() || null,
            model: model.trim() || null,
            fuelProductId: fuelProductId || null,
            note: note.trim() || null,
          },
        })
      ).data!,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.fleetPortal() });
      toast({ tone: "success", title: t("portal.requests.sent") });
      setPlate("");
      setFuelProductId("");
      setBrand("");
      setModel("");
      setNote("");
    },
    onError: (caught) => {
      if (isApiError(caught, "CONFLICT")) setPlateError(errorMessage(caught));
      else setError(errorMessage(caught));
    },
  });

  return (
    <Card className="p-4">
      <h2 className="mb-1 text-base font-semibold">{t("portal.requests.newTitle")}</h2>
      <p className="mb-4 text-sm text-muted-foreground">{t("portal.requests.newDescription")}</p>
      <form
        className="grid gap-4"
        noValidate
        aria-label={t("portal.requests.newTitle")}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          setPlateError(null);
          if (plate.trim() === "") {
            setPlateError(t("common.required"));
            return;
          }
          submit.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <FormField label={t("portal.fields.plate")} error={plateError ?? undefined} required>
            {(control) => (
              <PlateInput
                {...control}
                value={plate}
                onValueChange={setPlate}
                onValidityChange={setPlateError}
              />
            )}
          </FormField>
          <FormField label={t("portal.fields.fuel")} description={t("portal.requests.fuelHint")}>
            {(control) => (
              <Select
                {...control}
                value={fuelProductId}
                disabled={products.isPending}
                onChange={(event) => setFuelProductId(event.target.value)}
              >
                <option value="">{t("portal.requests.noFuel")}</option>
                {(products.data ?? []).map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("portal.fields.brand")}>
            {(control) => (
              <Input
                {...control}
                value={brand}
                maxLength={60}
                onChange={(event) => setBrand(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("portal.fields.model")}>
            {(control) => (
              <Input
                {...control}
                value={model}
                maxLength={60}
                onChange={(event) => setModel(event.target.value)}
              />
            )}
          </FormField>
        </div>
        <FormField label={t("portal.fields.note")} description={t("portal.requests.noteHint")}>
          {(control) => (
            <Textarea
              {...control}
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" loading={submit.isPending}>
            {t("portal.requests.submit")}
          </Button>
        </div>
      </form>
    </Card>
  );
}
